import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  useCancelAnalysisJob,
  useRerunAnalysisJob
} from "@/api/analysis/useAnalysisAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { CloseIcon } from "@/assets/icons";
import NotificationContent from "@/components/notification/notificationContent";
import { Button } from "@/components/ui/button";
import CollapseView from "@/components/ui/collapseView";
import { ERROR_MESSAGE, MESSAGE } from "@/constants/message";
import useLoadingAndToast from "@/hooks/ui/useLoadingAndToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { removeQueuedJobId } from "@/store/slice/analysisJob";
import { setDatasetHistory } from "@/store/slice/datasetSlice";
import {
  clearCurrRunningAnalysisId,
  setAnalysisResultOpenSource,
  setCurrRunningAnalysisId,
  setLatestAnalysisId
} from "@/store/slice/spatialSlice";
import { toggleAnalysisJobList } from "@/store/slice/uiSlice";
import { setWidgetHistory } from "@/store/slice/widgetSlice";
import { AnalysisJobItem, AnalysisJobStatus } from "@/types/analysis";
import { GetUserProfileData } from "@/types/userProfile";

import JobItem from "./jobItem";

export interface JobSection {
  title: string;
  jobs: AnalysisJobItem[];
  unOpenedJobIds?: string[];
  expanded?: boolean;
  userProfileData?: GetUserProfileData;
  onOpen?: (id: string) => void;
  onCancel?: (id: string) => void;
  onRerun?: (id: string) => void;
  completedList?: AnalysisJobItem[];
  isAnalysisHistoryEnabled?: boolean;
}

export const TEST_ID_PREFIX = "analysis-job-list";

const JobListSection = ({
  title,
  jobs,
  unOpenedJobIds,
  expanded = false,
  onOpen,
  onCancel,
  onRerun,
  userProfileData,
  completedList = [],
  isAnalysisHistoryEnabled
}: JobSection) => {
  const navigate = useNavigate();
  return (
    <CollapseView
      expanded={expanded}
      title={`${title} (${jobs.length || 0})`}
      className="p-0 text-sm font-semibold"
      titleClassName="text-sm"
    >
      <div className="flex flex-col gap-2">
        {jobs.map((job) => (
          <JobItem
            key={job.id}
            data={job}
            unOpenedJobIds={unOpenedJobIds}
            onOpen={onOpen}
            onCancel={onCancel}
            onRerun={onRerun}
            userProfileData={userProfileData}
          />
        ))}
        {isAnalysisHistoryEnabled &&
          title === "Completed" &&
          completedList?.length > 5 && (
            <div className="flex justify-center w-full">
              <Button
                variant="outline"
                className="w-full h-10 text-sm font-semibold rounded-xl"
                data-testid={`button-view-more-completed-jobs`}
                aria-label="View More Completed Jobs"
                onClick={() => navigate("/analysis-history")}
              >
                View More
              </Button>
            </div>
          )}
      </div>
    </CollapseView>
  );
};

export default function AnalysisJobList() {
  const { isAnalysisHistoryEnabled } = useAppFeatures();
  const dispatch = useDispatch();
  const { showError, showLoading, showWarning, clearLoadingState } =
    useLoadingAndToast();

  const { analysisJobs, unOpenedJobIds } = useSelector(
    (state: RootState) => state.analysisJob
  );
  const { currRunningAnalysisId } = useSelector(
    (state: RootState) => state.spatial
  );
  const { datasets, rawDatasets, visualizationOrder } = useSelector(
    (state: RootState) => state.dataset
  );
  const {
    widgets,
    visualizationOrder: widgetVisualizationOrder,
    widgetIndicates
  } = useSelector((state: RootState) => state.widget);

  const { data: userProfileData } = useGetUserProfileAPI();

  const runningList = analysisJobs.filter(
    (job) => job.status === AnalysisJobStatus.IN_PROGRESS
  );
  const waitingList = analysisJobs.filter(
    (job) => job.status === AnalysisJobStatus.QUEUED
  );
  const completedList = analysisJobs.filter(
    (job) => job.status === AnalysisJobStatus.SUCCESS
  );

  const sections: JobSection[] = [
    { title: "Running", jobs: runningList },
    { title: "Waiting", jobs: waitingList },
    {
      title: "Completed",
      jobs: isAnalysisHistoryEnabled ? completedList.slice(0, 5) : completedList
    }
  ];

  const handleErrorToast = () =>
    showError(
      <NotificationContent
        title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
        description={ERROR_MESSAGE.SOMETHING_WENT_WRONG_DESCRIPTION}
      />
    );

  const { mutate: cancelAnalysisJob } = useCancelAnalysisJob({
    onError: handleErrorToast,
    onSuccess: (_, { analysisId }) => {
      clearLoadingState();
      if (analysisId === currRunningAnalysisId) {
        dispatch(clearCurrRunningAnalysisId());
      }
    }
  });

  const { mutate: rerunAnalysisJob } = useRerunAnalysisJob({
    onError: handleErrorToast,
    onMutate: () => {
      showLoading(
        <NotificationContent
          title={MESSAGE.LOADING_SPATIAL_ANALYSIS}
          description={MESSAGE.PREPARE_TABULAR_DATA_AND_MAPVIEW}
        />
      );
    },
    onSuccess: (data) => {
      clearLoadingState();
      if (data?.message) {
        showWarning(
          <NotificationContent
            title="Queue Limit Reached"
            description={data.message}
          />
        );
      } else {
        // go to src/pages/home/index.tsx:71 to fetch analysis status
        dispatch(setCurrRunningAnalysisId(data.id));
        dispatch(setLatestAnalysisId(data.id));
        dispatch(
          setDatasetHistory({
            datasets: datasets,
            visualizationOrder: visualizationOrder,
            rawDatasets: rawDatasets
          })
        );
        dispatch(
          setWidgetHistory({
            widgets,
            visualizationOrder: widgetVisualizationOrder,
            widgetIndicates
          })
        );
      }
    }
  });

  return (
    <div className="absolute top-[30px] right-20 z-[9] border-gainsboro bg-white rounded-lg w-[368px] max-h-[calc(100vh-54px)] overflow-hidden p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-txtHeading">Analysis List</h3>
        <button
          className="text-sm"
          onClick={() => dispatch(toggleAnalysisJobList())}
        >
          <CloseIcon
            data-testid={`${TEST_ID_PREFIX}-close-icon`}
            className="inline-block"
          />
        </button>
      </div>

      <div className="overflow-y-auto custom-scrollbar max-h-[87vh] pr-2">
        {sections.map((section) => (
          <JobListSection
            key={section.title}
            {...section}
            unOpenedJobIds={unOpenedJobIds}
            expanded
            onOpen={(analysisId) => {
              // removeUnOpenedJobId handled on useAnalysisResult when stream:done
              dispatch(setAnalysisResultOpenSource("analysis-job-list"));
              dispatch(setCurrRunningAnalysisId(analysisId));
              dispatch(setLatestAnalysisId(analysisId));
            }}
            userProfileData={userProfileData}
            onCancel={(analysisId) => {
              dispatch(removeQueuedJobId(analysisId));
              cancelAnalysisJob({ analysisId });
            }}
            onRerun={(analysisId) => {
              rerunAnalysisJob({ analysisId });
            }}
            completedList={completedList}
            isAnalysisHistoryEnabled={isAnalysisHistoryEnabled}
          />
        ))}
      </div>
    </div>
  );
}
